import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HTTP_STATUS, ResponseError } from '../../../config/api';
import PlayerJoinLoadingOverlay from '../../shared/PlayerJoinLoadingOverlay';
import { getSamlSsoInitiate } from '../../../lib/api/public';
import { SamlSsoInitiateResponse } from '../../../lib/api/index';
import { ERROR_MESSAGE, INVALID_SSO_URL, SSO_NOT_CONFIGURED } from '../../../lib/constants';
import useSubdomain from '../../../hooks/use-subdomain';
import { AccountContextType, useAccountContext } from '../../../lib/context-providers/account-context';
import { getFirstPathSegmentFromLocation } from '../../../lib/helpers/urlHelper';


type Phase = 'loading' | 'submit' | 'error';

const useEventSubdomainForSso = (): string => {
  const hostSubdomain = useSubdomain()?.trim() ?? '';
  const { isAccountSubdomain } = useAccountContext() as AccountContextType;
  const pathEventSubdomain = getFirstPathSegmentFromLocation()?.trim() ?? '';

  return isAccountSubdomain ? pathEventSubdomain : hostSubdomain;
};

const SSOLoginPage = (): JSX.Element => {
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const clientId = params.get('client_id')?.trim() ?? '';
  const redirectPath = params.get('redirect_path')?.trim() ?? '';
  const subdomain = useEventSubdomainForSso();
  const paramsInvalid = !clientId || !subdomain;

  const [phase, setPhase] = useState<Phase>(paramsInvalid ? 'error' : 'loading');
  const [errorMessage, setErrorMessage] = useState<string>(paramsInvalid ? INVALID_SSO_URL : '');
  const [samlPayload, setSamlPayload] = useState<SamlSsoInitiateResponse | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const submitOnceRef = useRef(false);

  useEffect(() => {
    if (!clientId || !subdomain) {
      setPhase('error');
      setErrorMessage(INVALID_SSO_URL);
      return;
    }

    let cancelled = false;
    submitOnceRef.current = false;
    setSamlPayload(null);
    setPhase('loading');
    setErrorMessage('');

    (async (): Promise<void> => {
      try {
        const data = await getSamlSsoInitiate(clientId, redirectPath, subdomain);
        if (cancelled) return;

        const valid =
          typeof data?.idpSsoUrl === 'string' &&
          data.idpSsoUrl.length > 0 &&
          typeof data?.samlRequest === 'string' &&
          data.samlRequest.length > 0 &&
          typeof data?.relayState === 'string';

        if (!valid) {
          setPhase('error');
          setErrorMessage(ERROR_MESSAGE);
          return;
        }

        setSamlPayload(data);
        setPhase('submit');
      } catch (e) {
        if (cancelled) return;
        const err = e as ResponseError;
        if (err.status === HTTP_STATUS.NOT_FOUND) {
          setErrorMessage(SSO_NOT_CONFIGURED);
        } else {
          setErrorMessage(ERROR_MESSAGE);
        }
        setPhase('error');
      }
    })();

    return (): void => {
      cancelled = true;
    };
  }, [clientId, redirectPath, subdomain]);

  useEffect(() => {
    if (phase !== 'submit' || !samlPayload || submitOnceRef.current) return;
    submitOnceRef.current = true;
    if (formRef.current) {
      formRef.current.submit();
    }
  }, [phase, samlPayload]);

  if (phase === 'error') {
    return (
      <div className="flex flex-col items-center justify-center h-64 mt-16">
        <div className="flex mt-40 text-red-600 font-medium">
          <span>{errorMessage}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-64">
      <PlayerJoinLoadingOverlay visible />
      <form
        ref={formRef}
        method="post"
        action={samlPayload?.idpSsoUrl}
        className="hidden"
        aria-hidden="true"
      >
        <input type="hidden" name="SAMLRequest" value={samlPayload?.samlRequest} />
        <input type="hidden" name="RelayState" value={samlPayload?.relayState} />
      </form>
    </div>
  );
};

export default SSOLoginPage;
